import type { PortalLocale } from "./portalShell";

const EN = {
  loadingChat: "Loading chat",
  chatProfileLoadFailedTitle: "Couldn't load your chat profile",
  chatProfileLoadFailedBody:
    "We couldn't check your username for this group. Try again before entering chat.",
  pickUsernameTitle: "Pick a username for this group",
  pickUsernameBody: "Unique in this chat only — letters, numbers, and underscores.",
  chatUsernameLabel: "Chat username",
  chatUsernamePlaceholder: "e.g. physics_hero",
  joinChat: "Join chat",
  saving: "Saving…",
  attachImageAria: "Attach image",
  messagePlaceholder: "Message… @ to mention",
  sendMessageAria: "Send message",
  noMessagesYet: "No messages yet — say hello to your study group.",
  messagesLoadFailedTitle: "Couldn't load messages",
  messagesLoadFailedBody: "Try again before sending a message to this group.",
  oneTyping: (name: string) => `${name} is typing…`,
  manyTyping: (names: string) => `${names} are typing…`,
  membersTitle: "Members",
  membersDesc: "Tap someone to see their study stats.",
  membersLoadFailed: "Couldn't load members.",
  inviteLinkTitle: "Invite link",
  studyCentreMembersOnly: "study centre members only",
  inviteLinkDesc: "Only learners at your study centre can join with this link.",
  linkTypePermanent: "Permanent",
  linkTypeOneTime: "One-time",
  linkTypePermanentDesc: "Same link works until you regenerate it.",
  linkTypeOneTimeDesc:
    "Link expires after the next person joins; a new code is issued automatically.",
  copy: "Copy",
  copied: "Copied",
  savePreference: "Save preference",
  regenerateLink: "Regenerate link",
  removeFromGroup: "Remove from group",
  banFromGroup: "Ban from group",
  makeModerator: "Make moderator",
  removeModerator: "Remove moderator",
  updating: "Updating…",
  removing: "Removing…",
  banning: "Banning…",
  deleteMessage: "Delete message",
  messageDeletedBy: "This message was deleted by",
  messageDeletedFallback: "This message was deleted by someone.",
  messagesDeletedRun: (count: number, by: string | null) =>
    by ? `${count} messages were deleted by ${by}.` : `${count} messages were deleted.`,
  reconnecting: " · reconnecting…",
  membersCountPlural: (n: number) => `${n} member${n === 1 ? "" : "s"}`,
  failureProfanity: "Message blocked — inappropriate language.",
  failureMaliciousLink: "Link blocked — it failed our safety check.",
  failureNsfwImage: "Image blocked by moderation.",
  failureInvalidLink: "Only safe http(s) links are allowed.",
  messageCouldNotDeliver: "Message could not be delivered.",
  usernameTooShort: "Username must be at least 3 characters.",
  usernameSaveFailed: "Could not save username.",
  backToGroupsAria: "Back to groups",
  showGroupListAria: "Show group list",
  viewMembersAria: "View members",
  mentionMemberAria: "Mention a member",
  messageActionsAria: "Message actions",
  imagePreviewAria: "Image preview",
  closeAria: "Close",
  processingAria: "Processing",
  viewReceiptsAria: "View read receipts",
  checkingLinks: "Checking links…",
  verifiedLinkAria: (host: string) => `Verified link: ${host}, opens in a new tab`,
  youLabel: "You",
  groupAdmin: "Group admin",
  moderator: "Moderator",
  memberActionsAria: (name: string) => `Actions for ${name}`,
  sharedImageAlt: "Shared image",
  copyInviteFailed: "Could not copy invite link.",
  sendMessageFailed: "Could not send message.",
  sendImageFailed: "Could not send image.",
  messageInfo: "Message info",
  messageInfoDesc: "Seen, delivered, and pending for each member.",
  download: "Download",
  receiptSeen: "Seen",
  receiptDelivered: "Delivered — not opened yet",
  receiptPending: "Still processing",
  receiptNotDelivered: "Not delivered",
  receiptsEmpty: "No other members in this group.",
  receiptsLoadFailed: "Couldn't load read receipts.",
  linkTypeLabel: "Link type",
  memberStatsDesc: "Study progress — keep each other motivated.",
  statDueNow: "Due now",
  statReviewedToday: "Reviewed today",
  statDayStreak: "Day streak",
  statActivePlans: "Active plans",
  statTotalCards: "Total cards",
  morePlans: (n: number) => `+${n} more plan${n === 1 ? "" : "s"}`,
} as const;

type StudyGroupChatTable = {
  [K in keyof typeof EN]: (typeof EN)[K] extends (...args: infer A) => infer R
    ? (...args: A) => R
    : string;
};

const HI: StudyGroupChatTable = {
  loadingChat: "चैट लोड हो रहा है",
  chatProfileLoadFailedTitle: "चैट प्रोफ़ाइल लोड नहीं हुई",
  chatProfileLoadFailedBody:
    "इस समूह के लिए आपका यूज़रनेम जाँचा नहीं जा सका। चैट खोलने से पहले फिर कोशिश करें।",
  pickUsernameTitle: "इस समूह के लिए एक यूज़रनेम चुनें",
  pickUsernameBody: "केवल इस चैट में अनोखा — अक्षर, संख्या, और अंडरस्कोर।",
  chatUsernameLabel: "चैट यूज़रनेम",
  chatUsernamePlaceholder: "उदा. physics_hero",
  joinChat: "चैट में जाएँ",
  saving: "सेव हो रहा है…",
  attachImageAria: "छवि लगाएँ",
  messagePlaceholder: "संदेश… @ से मेंशन करें",
  sendMessageAria: "संदेश भेजें",
  noMessagesYet: "अभी कोई संदेश नहीं — समूह को नमस्ते कहें।",
  messagesLoadFailedTitle: "संदेश लोड नहीं हुए",
  messagesLoadFailedBody: "इस समूह में संदेश भेजने से पहले फिर कोशिश करें।",
  oneTyping: (name: string) => `${name} लिख रहे हैं…`,
  manyTyping: (names: string) => `${names} लिख रहे हैं…`,
  membersTitle: "सदस्य",
  membersDesc: "किसी पर टैप करें और उनके अध्ययन आँकड़े देखें।",
  membersLoadFailed: "सदस्य लोड नहीं हुए।",
  inviteLinkTitle: "इनवाइट लिंक",
  studyCentreMembersOnly: "केवल स्टडी सेंटर सदस्यों के लिए",
  inviteLinkDesc: "केवल आपके स्टडी सेंटर के लर्नर इस लिंक से जुड़ सकते हैं।",
  linkTypePermanent: "स्थायी",
  linkTypeOneTime: "एक बार का",
  linkTypePermanentDesc: "रीजनरेट करने तक वही लिंक काम करेगा।",
  linkTypeOneTimeDesc: "लिंक अगले सदस्य के जुड़ने पर समाप्त हो जाएगा; नया कोड अपने आप बनता है।",
  copy: "कॉपी",
  copied: "कॉपी हुआ",
  savePreference: "सेव करें",
  regenerateLink: "लिंक रीजनरेट करें",
  removeFromGroup: "समूह से हटाएँ",
  banFromGroup: "समूह से प्रतिबंधित करें",
  makeModerator: "मॉडरेटर बनाएँ",
  removeModerator: "मॉडरेटर हटाएँ",
  updating: "अपडेट हो रहा है…",
  removing: "हटा रहे हैं…",
  banning: "प्रतिबंध लगा रहे हैं…",
  deleteMessage: "संदेश हटाएँ",
  messageDeletedBy: "यह संदेश हटाया गया था",
  messageDeletedFallback: "यह संदेश किसी द्वारा हटाया गया था।",
  messagesDeletedRun: (count: number, by: string | null) =>
    by ? `${count} संदेश ${by} द्वारा हटाए गए।` : `${count} संदेश हटाए गए।`,
  reconnecting: " · फिर कनेक्ट कर रहा है…",
  membersCountPlural: (n: number) => `${n} सदस्य`,
  failureProfanity: "मैसेज ब्लॉक — अनुपयुक्त भाषा।",
  failureMaliciousLink: "लिंक ब्लॉक — हमारी सुरक्षा जांच फेल हुई।",
  failureNsfwImage: "मॉडरेशन ने छवि ब्लॉक कर दी।",
  failureInvalidLink: "केवल सुरक्षित http(s) लिंक स्वीकार्य हैं।",
  messageCouldNotDeliver: "संदेश भेजा नहीं जा सका।",
  usernameTooShort: "यूज़रनेम कम से कम 3 अक्षर का होना चाहिए।",
  usernameSaveFailed: "यूज़रनेम सेव नहीं हो सका।",
  backToGroupsAria: "समूहों पर वापस",
  showGroupListAria: "समूह सूची दिखाएँ",
  viewMembersAria: "सदस्य देखें",
  mentionMemberAria: "किसी सदस्य को मेंशन करें",
  messageActionsAria: "संदेश विकल्प",
  imagePreviewAria: "छवि पूर्वावलोकन",
  closeAria: "बंद करें",
  processingAria: "प्रोसेस हो रहा है",
  viewReceiptsAria: "रीड रिसीट देखें",
  checkingLinks: "लिंक जाँचे जा रहे हैं…",
  verifiedLinkAria: (host: string) => `सत्यापित लिंक: ${host}, नई टैब में खुलता है`,
  youLabel: "आप",
  groupAdmin: "समूह एडमिन",
  moderator: "मॉडरेटर",
  memberActionsAria: (name: string) => `${name} के लिए विकल्प`,
  sharedImageAlt: "साझा की गई छवि",
  copyInviteFailed: "इनवाइट लिंक कॉपी नहीं हो सका।",
  sendMessageFailed: "संदेश भेजा नहीं जा सका।",
  sendImageFailed: "छवि भेजी नहीं जा सकी।",
  messageInfo: "संदेश जानकारी",
  messageInfoDesc: "हर सदस्य के लिए देखा गया, पहुँचा और बाकी स्थिति।",
  download: "डाउनलोड",
  receiptSeen: "देखा गया",
  receiptDelivered: "पहुँचा — अभी खोला नहीं",
  receiptPending: "अभी प्रोसेस हो रहा है",
  receiptNotDelivered: "नहीं पहुँचा",
  receiptsEmpty: "इस समूह में कोई अन्य सदस्य नहीं है।",
  receiptsLoadFailed: "रीड रिसीट लोड नहीं हुईं।",
  linkTypeLabel: "लिंक प्रकार",
  memberStatsDesc: "अध्ययन प्रगति — एक-दूसरे को प्रेरित रखें।",
  statDueNow: "अभी बाकी",
  statReviewedToday: "आज दोहराया",
  statDayStreak: "दिन की लय",
  statActivePlans: "सक्रिय प्लान",
  statTotalCards: "कुल कार्ड",
  morePlans: (n: number) => `+${n} और प्लान`,
};

const TABLES: Record<PortalLocale, StudyGroupChatTable> = {
  en: EN,
  hi: HI,
};

export function studyGroupChatStrings(locale: PortalLocale): StudyGroupChatTable {
  return TABLES[locale] ?? EN;
}
